import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTokens } from '../../styles/tokens';
import { useRoom } from '../../hooks/useRoom';
import { useToast } from '../common/Toast';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Avatar } from '../common/Avatar';
import { Loader } from '../common/Loader';
import { Navbar } from '../common/Navbar';
import { NamePromptModal } from '../common/NamePromptModal';
import { ConfirmModal } from '../common/ConfirmModal';
import { PokerTable } from './PokerTable';
import { CardDeck } from './CardDeck';
import { DeckSelector } from './DeckSelector';
import { VotingResults } from './VotingResults';
import { VotingHistory } from './VotingHistory';
import { TalkSuggestions } from './TalkSuggestions';
import { getRandomAnimal } from '../../../domain/valueObjects/AnimalAvatars';
import { ThrowEvent, ReactionEvent, DeckType } from '../../../domain/entities/Room';
import { getCardLabel } from '../../i18n/cardLabels';

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const VotesPill = styled.span<{ tokens: any }>`
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.tokens.textMuted};
  background: ${p => p.tokens.bgElevated};
  border: 1px solid ${p => p.tokens.border};
  padding: 6px 12px;
  border-radius: 16px;
  height: 32px;
  display: flex;
  align-items: center;
`;

const Body = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 260px;
  gap: 0;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Main = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media (min-height: 900px) {
    padding: 24px;
    gap: 20px;
  }
`;

const Sidebar = styled.div<{ surface: string; border: string }>`
  background: ${p => p.surface};
  border-left: 1px solid ${p => p.border};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media (max-width: 900px) {
    border-left: none;
    border-top: 1px solid ${p => p.border};
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px 20px;
  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StoryArea = styled.div`
  flex: 1;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RoomTitle = styled.h2<{ color: string }>`
  font-size: 15px;
  font-weight: 800;
  color: ${p => p.color};
  margin: 0;
  flex-shrink: 0;
`;

const SideTitle = styled.h3<{ color: string }>`
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.color};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ParticipantRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
`;

const ParticipantName = styled.span<{ color: string }>`
  font-size: 14px;
  color: ${p => p.color};
  flex: 1;
`;

const VoteIndicator = styled.span<{ hasVote: boolean; tokens: any }>`
  font-size: 12px;
  font-weight: 700;
  color: ${p => p.hasVote ? p.tokens.success : p.tokens.textFaint};
`;

const ConnectionDot = styled.span<{ connected: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.connected ? '#10b981' : '#94a3b8'};
  display: inline-block;
`;

const StoryInput = styled.div`
  display: flex;
  gap: 8px;
  & > input {
    flex: 1;
    min-width: 0;
  }
`;

const SectionLabel = styled.div<{ color: string }>`
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.color};
`;

const AdminBadge = styled.span<{ tokens: any }>`
  font-size: 10px;
  background: ${p => p.tokens.primaryDark};
  color: #fff;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
`;

const SharePanel = styled.div<{ tokens: any }>`
  background: ${p => p.tokens.bgElevated};
  border: 1px solid ${p => p.tokens.border};
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ShareLabel = styled.div<{ color: string }>`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${p => p.color};
`;

const ShareCode = styled.div<{ color: string }>`
  font-family: monospace;
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.color};
  word-break: break-all;
  line-height: 1.4;
`;

const ShareHint = styled.div<{ color: string }>`
  font-size: 11px;
  color: ${p => p.color};
`;

export const GameRoom: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const { user, signIn } = useAuth();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const tok = getTokens(theme);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { room, loading, joinRoom, vote, reveal, reset, setStory, setDeckType, leaveRoom, deleteRoom, sendThrow, sendReaction } = useRoom(roomId || null);

  const [storyInput, setStoryInput] = useState('');
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [activeThrows, setActiveThrows] = useState<Array<{ id: string; fromUserId: string; toUserId: string; emoji: string }>>([]);
  const [activeReactions, setActiveReactions] = useState<Array<{ id: string; userId: string; emoji: string }>>([]);
  const [pendingDeck, setPendingDeck] = useState<DeckType | null>(null);
  const seenEventIds = useRef(new Set<string>());

  const isAdmin = room?.adminId === user?.id;
  const myParticipant = room?.participants?.[user?.id || ''];
  const participants = Object.values(room?.participants || {});
  const allVoted = participants.length > 0 && participants.every(p => p.vote !== null);
  const voteCount = participants.filter(p => p.vote !== null).length;
  const revealed = room?.status === 'revealed';
  const deckType: DeckType = room?.deckType || 'fibonacci';

  useEffect(() => {
    if (!user || !roomId) return;
    if (room && !myParticipant) {
      joinRoom(roomId, user.id, user.name, getRandomAnimal(), false);
    }
  }, [room, user, roomId, myParticipant, joinRoom]);

  useEffect(() => {
    if (room?.currentStory) {
      setStoryInput(room.currentStory);
    }
  }, [room?.currentStory]);

  useEffect(() => {
    setSelectedCard(myParticipant?.vote || null);
  }, [myParticipant?.vote]);

  useEffect(() => {
    if (!room?.throws) return;
    Object.values(room.throws).forEach(evt => {
      if (seenEventIds.current.has(evt.id)) return;
      seenEventIds.current.add(evt.id);
      setActiveThrows(prev => [...prev, evt]);
      setTimeout(() => setActiveThrows(prev => prev.filter(t => t.id !== evt.id)), 2800);
    });
  }, [room?.throws]);

  useEffect(() => {
    if (!room?.reactions) return;
    Object.values(room.reactions).forEach(evt => {
      if (seenEventIds.current.has(evt.id)) return;
      seenEventIds.current.add(evt.id);
      setActiveReactions(prev => [...prev, evt]);
      setTimeout(() => setActiveReactions(prev => prev.filter(r => r.id !== evt.id)), 2800);
    });
  }, [room?.reactions]);

  // Show name modal if user has no identity yet (direct link entry)
  if (!user) {
    return (
      <Wrapper>
        <Navbar tokens={tok} onLogoClick={() => navigate('/')} />
        <NamePromptModal tokens={tok} onSubmit={(name) => signIn(name)} />
      </Wrapper>
    );
  }

  if (loading) return <Loader tokens={tok} fullPage />;
  if (!room) return (
    <Wrapper>
      <Navbar tokens={tok} onLogoClick={() => navigate('/')} />
      <Main style={{ alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <div style={{ color: tok.text, fontSize: 20, fontWeight: 700 }}>{t.roomNotFound}</div>
        <Button tokens={tok} onClick={() => navigate('/lobby')} style={{ marginTop: 16 }}>{t.backToLobby}</Button>
      </Main>
    </Wrapper>
  );

  const handleVote = async (card: string) => {
    if (revealed || !user) return;
    setSelectedCard(card);
    try {
      await vote(roomId!, user.id, card, deckType);
    } catch (err: any) {
      showToast(err?.message || 'error', 'error');
      setSelectedCard(myParticipant?.vote || null);
    }
  };

  const handleReveal = async () => {
    if (!isAdmin || !room) return;
    const votes: Record<string, string> = {};
    participants.forEach(p => { if (p.vote) votes[p.userId] = p.vote; });
    await reveal(roomId!, room.currentStory, votes, deckType);
    showToast(t.votesRevealedMsg, 'success');
  };

  const handleReset = async () => {
    setSelectedCard(null);
    await reset(roomId!, storyInput);
  };

  const handleDeckChange = (next: DeckType) => {
    if (!isAdmin || !room) return;
    if (next === deckType) return;
    const hasVotes = participants.some(p => p.vote !== null) || revealed;
    if (hasVotes) {
      setPendingDeck(next);
    } else {
      setDeckType(roomId!, next).catch(() => showToast('error', 'error'));
    }
  };

  const confirmDeckChange = async () => {
    if (!pendingDeck) return;
    const next = pendingDeck;
    setPendingDeck(null);
    setSelectedCard(null);
    try {
      await setDeckType(roomId!, next);
    } catch {
      showToast('error', 'error');
    }
  };

  const handleSetStory = async () => {
    if (!storyInput.trim()) return;
    await setStory(roomId!, storyInput.trim());
    showToast(t.storyUpdatedMsg, 'success');
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/room/${roomId}`;
    navigator.clipboard.writeText(url);
    showToast(t.linkCopiedMsg, 'success');
  };

  const handleThrow = async (toUserId: string, emoji: string) => {
    if (!user || !roomId) return;
    const event: ThrowEvent = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2),
      fromUserId: user.id,
      fromName: user.name,
      toUserId,
      emoji,
      timestamp: Date.now(),
    };
    await sendThrow(roomId, event);
  };

  const handleReaction = async (emoji: string) => {
    if (!user || !roomId) return;
    const event: ReactionEvent = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2),
      userId: user.id,
      userName: user.name,
      emoji,
      timestamp: Date.now(),
    };
    await sendReaction(roomId, event);
  };

  const handleLeave = async () => {
    if (!user) return;
    if (isAdmin) {
      await deleteRoom(roomId!);
    } else {
      await leaveRoom(roomId!, user.id);
    }
    navigate('/lobby');
  };

  const historyArr = room.votingHistory
    ? Object.values(room.votingHistory)
    : [];

  const currentRound = revealed && historyArr.length > 0
    ? historyArr.reduce((a, b) => a.completedAt > b.completedAt ? a : b)
    : null;

  const participantNames: Record<string, string> = {};
  participants.forEach(p => { participantNames[p.userId] = p.name; });

  const inviteUrl = `${window.location.origin}/room/${roomId}`;

  return (
    <Wrapper>
      <Navbar
        tokens={tok}
        onLogoClick={() => navigate('/')}
        rightContent={
          <>
            <VotesPill tokens={tok}>{t.votesCounter(voteCount, participants.length)}</VotesPill>
            <Button tokens={tok} variant="danger" size="sm" onClick={handleLeave}>
              {isAdmin ? t.closeRoomBtn : t.leaveBtn}
            </Button>
          </>
        }
      />

      <Body>
        <Main>
          <HeaderRow>
            <RoomTitle color={tok.text}>{room.name}</RoomTitle>
            {isAdmin && (
              <StoryArea>
                <SectionLabel color={tok.textMuted}>{t.currentStoryLabel}</SectionLabel>
                <StoryInput>
                  <Input
                    tokens={tok}
                    placeholder={t.storyPlaceholder}
                    value={storyInput}
                    onChange={e => setStoryInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSetStory()}
                  />
                  <Button tokens={tok} size="sm" onClick={handleSetStory}>
                    {t.setStoryBtn}
                  </Button>
                </StoryInput>
              </StoryArea>
            )}
            {isAdmin && (
              <DeckSelector
                tokens={tok}
                currentDeck={deckType}
                onChange={handleDeckChange}
              />
            )}
          </HeaderRow>

          <PokerTable
            participants={room.participants || {}}
            currentStory={room.currentStory}
            revealed={revealed}
            tokens={tok}
            currentUserId={user.id}
            activeThrows={activeThrows}
            activeReactions={activeReactions}
            onThrow={handleThrow}
            onReact={handleReaction}
            deckType={deckType}
          />

          {revealed && currentRound && (
            <VotingResults
              round={currentRound}
              participantNames={participantNames}
              tokens={tok}
            />
          )}

          {revealed && currentRound && (
            <TalkSuggestions
              votes={currentRound.votes}
              participantNames={participantNames}
              tokens={tok}
              deckType={currentRound.deckType || 'fibonacci'}
            />
          )}

          {!revealed && (
            <div>
              <CardDeck
                selectedCard={selectedCard}
                onSelect={handleVote}
                disabled={revealed}
                tokens={tok}
                deckType={deckType}
              />
            </div>
          )}

          {isAdmin && (
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              {!revealed ? (
                <Button
                  tokens={tok}
                  size="md"
                  disabled={voteCount === 0}
                  onClick={handleReveal}
                >
                  {allVoted ? t.revealVotesBtn : t.revealWithCount(voteCount)}
                </Button>
              ) : (
                <Button tokens={tok} size="md" variant="secondary" onClick={handleReset}>
                  {t.newRoundBtn}
                </Button>
              )}
            </div>
          )}

          <VotingHistory history={historyArr} tokens={tok} />
        </Main>

        <Sidebar surface={tok.bgSurface} border={tok.border}>
          <SharePanel tokens={tok}>
            <ShareLabel color={tok.textMuted}>{t.shareWithTeam}</ShareLabel>
            <ShareCode color={tok.primary}>{inviteUrl}</ShareCode>
            <Button tokens={tok} size="sm" fullWidth onClick={handleCopyLink}>
              {t.copyLinkBtn}
            </Button>
            <ShareHint color={tok.textFaint}>{t.shareWithTeam}</ShareHint>
          </SharePanel>

          <SideTitle color={tok.textMuted}>{t.participantsTitle(participants.length)}</SideTitle>
          {participants.map(p => (
            <ParticipantRow key={p.userId}>
              <ConnectionDot connected={p.isConnected} />
              <Avatar animal={p.animal} name={p.name} size={28} tokens={tok} />
              <ParticipantName color={tok.text}>
                {p.name.split(' ')[0]}
                {p.isAdmin && <AdminBadge tokens={tok} style={{ marginLeft: 4 }}>{t.adminBadge}</AdminBadge>}
              </ParticipantName>
              <VoteIndicator hasVote={!!p.vote} tokens={tok}>
                {revealed && p.vote ? getCardLabel(p.vote, deckType, t) : (p.vote ? '✓' : '–')}
              </VoteIndicator>
            </ParticipantRow>
          ))}
        </Sidebar>
      </Body>

      {pendingDeck && (
        <ConfirmModal
          tokens={tok}
          title={t.deck.changeConfirmTitle}
          body={t.deck.changeConfirmBody}
          confirmLabel={t.deck.changeConfirmCta}
          cancelLabel={t.deck.cancel}
          onConfirm={confirmDeckChange}
          onCancel={() => setPendingDeck(null)}
        />
      )}
    </Wrapper>
  );
};
